import type { GatewayMessage } from '@/types/omni';

/**
 * 全站统一 WebSocket 网关客户端。
 * 负责唯一连接、状态同步、重连、心跳、待发送消息和通道分发。
 */

export interface WsStatusState {
  connected: boolean;
  readyState: number;
  code?: number;
  reason?: string;
}

export type WsChannelListener<T = any> = (data: T) => void;
export type WsStatusListener = (status: WsStatusState) => void;

export interface WsSendOptions {
  queue?: boolean;
}

const MAX_OUTGOING_QUEUE = 200;
const HEARTBEAT_INTERVAL = 25000;
const HEARTBEAT_TIMEOUT = 65000;
const MAX_RECONNECT_DELAY = 30000;

export class WebSocketGateway {
  private ws: WebSocket | null = null;
  private channelListeners: Map<string, Set<WsChannelListener>> = new Map();
  private statusListeners: Set<WsStatusListener> = new Set();
  private outgoingQueue: GatewayMessage[] = [];
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 20;
  private reconnectTimer: any = null;
  private heartbeatTimer: any = null;
  private lastPongAt = 0;
  private isManualDisconnect = false;
  private isAuthBlocked = false;
  private consecutiveHandshakeFailures = 0;
  public isConnected = false;
  private connectionGeneration = 0;

  constructor() {
    this._setupVisibilityListener();
  }

  private _setupVisibilityListener(): void {
    if (typeof document === 'undefined') return;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible' || this.isManualDisconnect || this.isAuthBlocked) return;
      const token = localStorage.getItem('token');
      if (token && (!this.ws || this.ws.readyState === WebSocket.CLOSED)) {
        this.reconnectAttempts = 0;
        this.connect();
      }
    });
  }

  public connect(): boolean {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token || this.isAuthBlocked) {
      if (this.isAuthBlocked) {
        console.warn('[WebSocketGateway] 鉴权已被拒绝，阻止继续重连');
      }
      this._setConnectionState(false, this._getClosedState());
      return false;
    }

    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN) {
        this._setConnectionState(true, WebSocket.OPEN);
        return true;
      }
      if (this.ws.readyState === WebSocket.CONNECTING) {
        this._setConnectionState(false, WebSocket.CONNECTING);
        return false;
      }
    }

    this.isManualDisconnect = false;
    const generation = ++this.connectionGeneration;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws/gateway?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);
    this.ws = ws;
    this._setConnectionState(false, WebSocket.CONNECTING);

    ws.onopen = () => {
      if (!this._isCurrent(ws, generation)) return;
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.consecutiveHandshakeFailures = 0;
      this.lastPongAt = Date.now();
      this._clearReconnectTimer();
      this._startHeartbeat();
      this._setConnectionState(true, WebSocket.OPEN);
      this._flushQueue(ws, generation);
    };

    ws.onmessage = (event: MessageEvent) => {
      if (!this._isCurrent(ws, generation)) return;
      this.lastPongAt = Date.now();
      try {
        const raw = event.data;
        if (raw === 'pong') return;
        const data = JSON.parse(raw);
        if (data.action === 'pong') return;

        if (data.action === 'auth_error' || data.code === 4001 || data.code === 401) {
          console.warn('[WebSocketGateway] 收到鉴权失败通知:', data.reason);
          this._handleAuthenticationFailure({
            code: 4001,
            reason: data.reason || 'Token 无效或已过期'
          });
          return;
        }

        const channel = data.channel || (data.type ? 'agent' : 'message');
        this._emitChannel(channel, data);
      } catch (error) {
        console.error('[WebSocketGateway] 解析消息失败:', error);
      }
    };

    ws.onerror = (error: Event) => {
      if (!this._isCurrent(ws, generation)) return;
      console.warn('[WebSocketGateway] 连接异常:', error);
    };

    ws.onclose = (event: CloseEvent) => {
      if (!this._isCurrent(ws, generation)) return;
      this.ws = null;
      this.isConnected = false;
      this._stopHeartbeat();
      this._setConnectionState(false, WebSocket.CLOSED, event.code, event.reason);

      if (this.isManualDisconnect || this.isAuthBlocked) return;

      const isAuthCode = event.code === 4001 || event.code === 4003;
      const isAuthReason = Boolean(
        event.reason && (event.reason.includes('Token') || event.reason.includes('过期') || event.reason.includes('认证'))
      );

      if (isAuthCode || isAuthReason) {
        this._handleAuthenticationFailure(event);
        return;
      }

      this.consecutiveHandshakeFailures += 1;
      if (this.consecutiveHandshakeFailures >= 5) {
        console.warn('[WebSocketGateway] 连续握手失败超过阈值，暂停自动重连');
        this.disconnect();
        return;
      }

      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      this._scheduleReconnect();
    };

    return false;
  }

  public resetAuth(): void {
    this.isAuthBlocked = false;
    this.isManualDisconnect = false;
    this.reconnectAttempts = 0;
    this.consecutiveHandshakeFailures = 0;
    this._clearReconnectTimer();
  }

  public reconnect(): boolean {
    this.resetAuth();
    this._stopHeartbeat();
    this.connectionGeneration += 1;

    const oldWs = this.ws;
    this.ws = null;
    this.isConnected = false;
    if (oldWs) {
      try {
        oldWs.close(1000, 'client reconnect');
      } catch (error) {
        // ignore close errors
      }
    }
    this._setConnectionState(false, this._getClosedState());
    return this.connect();
  }

  public disconnect(): void {
    this.isManualDisconnect = true;
    this._clearReconnectTimer();
    this._stopHeartbeat();
    this.connectionGeneration += 1;

    const oldWs = this.ws;
    this.ws = null;
    this.isConnected = false;
    if (oldWs) {
      try {
        oldWs.close(1000, 'client disconnect');
      } catch (error) {
        // ignore close errors
      }
    }
    this.outgoingQueue = [];
    this._setConnectionState(false, this._getClosedState());
  }

  /**
   * 发送通道消息。连接尚未打开时默认排队，连接建立后自动发送。
   */
  public send(channel: string, payload: Record<string, any> = {}, options: WsSendOptions = {}): boolean {
    const packet: GatewayMessage = { ...payload, channel };
    const queueWhenDisconnected = options.queue !== false;

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return this._sendPacket(this.ws, packet);
    }

    if (queueWhenDisconnected) {
      if (this.outgoingQueue.length >= MAX_OUTGOING_QUEUE) {
        this.outgoingQueue.shift();
      }
      this.outgoingQueue.push(packet);
    }
    return false;
  }

  public on<T = any>(channel: string, callback: WsChannelListener<T>): () => void {
    if (!this.channelListeners.has(channel)) {
      this.channelListeners.set(channel, new Set());
    }
    this.channelListeners.get(channel)!.add(callback);
    return () => this.off(channel, callback);
  }

  public off(channel: string, callback: WsChannelListener): void {
    const listeners = this.channelListeners.get(channel);
    if (!listeners) return;
    listeners.delete(callback);
    if (listeners.size === 0) this.channelListeners.delete(channel);
  }

  public onStatus(callback: WsStatusListener): () => void {
    this.statusListeners.add(callback);
    try {
      callback({
        connected: this.isConnected,
        readyState: this.getReadyState()
      });
    } catch (error) {
      // ignore listener errors
    }
    return () => this.offStatus(callback);
  }

  public offStatus(callback: WsStatusListener): void {
    this.statusListeners.delete(callback);
  }

  private _isCurrent(ws: WebSocket, generation: number): boolean {
    return this.ws === ws && this.connectionGeneration === generation;
  }

  private _sendPacket(ws: WebSocket, packet: GatewayMessage): boolean {
    try {
      ws.send(JSON.stringify(packet));
      return true;
    } catch (error) {
      console.error(`[WebSocketGateway] 发送 ${packet.channel} 消息失败:`, error);
      return false;
    }
  }

  private _flushQueue(ws: WebSocket, generation: number): void {
    if (!this._isCurrent(ws, generation)) return;
    while (this.outgoingQueue.length > 0 && ws.readyState === WebSocket.OPEN) {
      const packet = this.outgoingQueue.shift();
      if (packet && !this._sendPacket(ws, packet)) {
        this.outgoingQueue.unshift(packet);
        break;
      }
    }
  }

  private _handleAuthenticationFailure(event: { code?: number; reason?: string }): void {
    this.isAuthBlocked = true;
    this._clearReconnectTimer();
    this.reconnectAttempts = 0;
    this.outgoingQueue = [];

    const oldWs = this.ws;
    this.ws = null;
    this.isConnected = false;
    if (oldWs) {
      try {
        oldWs.close();
      } catch (error) {
        // ignore close errors
      }
    }
    this._stopHeartbeat();
    this._setConnectionState(false, this._getClosedState(), event?.code, event?.reason);

    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('token');
      }
    } catch (error) {
      // ignore storage errors
    }

    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      const detail = {
        code: event?.code || 4001,
        reason: event?.reason || 'WebSocket 认证失败'
      };
      try {
        window.dispatchEvent(new CustomEvent('websocket-auth-failed', { detail }));
      } catch (error) {
        // CustomEvent may be unavailable in non-browser test environments
      }
    }
  }

  private _scheduleReconnect(): void {
    if (this.reconnectTimer || this.isManualDisconnect || this.isAuthBlocked) return;
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) return;

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn('[WebSocketGateway] 达到最大重连次数，已暂停自动重连');
      return;
    }

    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), MAX_RECONNECT_DELAY);
    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private _clearReconnectTimer(): void {
    if (!this.reconnectTimer) return;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private _startHeartbeat(): void {
    this._stopHeartbeat();
    this.lastPongAt = Date.now();
    this.heartbeatTimer = setInterval(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
      if (Date.now() - this.lastPongAt > HEARTBEAT_TIMEOUT) {
        try {
          this.ws.close(4000, 'heartbeat timeout');
        } catch (error) {
          // ignore close errors
        }
        return;
      }
      this._sendPacket(this.ws, {
        channel: 'gateway',
        action: 'ping',
        timestamp: Date.now()
      });
    }, HEARTBEAT_INTERVAL);
  }

  private _stopHeartbeat(): void {
    if (!this.heartbeatTimer) return;
    clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
  }

  private _setConnectionState(connected: boolean, readyState: number, code?: number, reason?: string): void {
    this.isConnected = connected;
    this._emitStatus(connected, code, reason, readyState);
  }

  private _emitChannel(channel: string, data: any): void {
    const listeners = this.channelListeners.get(channel);
    if (!listeners) return;
    listeners.forEach((listener) => {
      try {
        listener(data);
      } catch (error) {
        console.error(`[WebSocketGateway] channel ${channel} listener error:`, error);
      }
    });
  }

  private _emitStatus(connected: boolean, code?: number, reason?: string, readyState: number = this.getReadyState()): void {
    this.statusListeners.forEach((listener) => {
      try {
        listener({ connected, readyState, code, reason });
      } catch (error) {
        console.error('[WebSocketGateway] status listener error:', error);
      }
    });
  }

  private _getClosedState(): number {
    return typeof WebSocket !== 'undefined' ? WebSocket.CLOSED : 3;
  }

  public getReadyState(): number {
    return this.ws ? this.ws.readyState : this._getClosedState();
  }
}

const wsGateway = new WebSocketGateway();

export default wsGateway;
